/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import forge from 'node-forge';
import store from '@/store';

/**
 * PKCE support service class.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
class Pkce {
    /**
     * Base64 encode as string.
     *
     * @param {string} str
     * @return {string}
     */
    public base64URLEncode(str: string): string {
        return btoa(str)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    /**
     * Create verifier.
     *
     * @return {string}
     */
    public createVerifier(): string {
        const randBytes: any = forge.random.getBytesSync(32);
        return this.base64URLEncode(randBytes);
    }

    /**
     * Create challenge.
     *
     * @param {string} verifier
     * @return {string}
     */
    public createChallenge(verifier: string): string {
        const md: any = forge.md.sha256.create();
        md.update(verifier);
        return this.base64URLEncode(md.digest().getBytes());
    }

    /**
     * Create state.
     *
     * To create state we use the on-way hash (md5) of the verifier.
     * As a MD5 hash cannot be decoded this is deemed a safe state to produce.
     *
     * @param {string} verifier
     *
     * @return {string}
     */
    public createState(verifier: string): string {
        const md: any = forge.md.md5.create();
        md.update(verifier);
        return md.digest().toHex();
    }

    /**
     * Get a new oAuth authentication URL.
     *
     * @return {string}
     */
    public getAuthUrl(): string {
        const verifier: string = this.createVerifier();
        const challenge: string = this.createChallenge(verifier);
        const clientId: string = process.env.VUE_APP_CLIENTID || '';
        const state: string = this.createState(verifier);

        const params: string[] = [
            // 'audience=steam', // Use this to limit auth providers
            'response_type=code',
            `state=${state}`,
            `client_id=${clientId}`,
            `code_challenge=${challenge}`,
            'code_challenge_method=S256',
            `redirect_uri=${this.redirectUrl}`,
        ];

        // add to store
        store.dispatch('userSessionModule/updateVerifier', verifier);

        return `${process.env.VUE_APP_AUTH || ''}/auth?${params.join('&')}`;
    }

    /**
     * Get redirection url.
     *
     * @returns {string}
     */
    get redirectUrl(): string {
        return `${window.location.origin}/authenticate`;
    }
}

export default new Pkce();
